import React, { useCallback, useEffect, useRef } from "react";
import {
  SimpleInfoAlert,
  SimpleInfoAlertType
} from "../../../../Shared/SimpleInfoAlert/React";
import { ValidatedSelectInputField } from "../../components/fields/validatedSelectInputField";
import { ValidatedTextAreaField } from "../../components/fields/validatedTextAreaField";
import { ValidatedTextInputField } from "../../components/fields/validatedTextInputField";
import { useTranslationContext } from "../../context";
import { FormSelectItem, OnlineBookingPatientValidation } from "../../types";
import { ProvetValidationUtilities } from "../../utilities";
import { removeEmoji } from "./helpers/contactDetailsUtility";
import { useContactDetailsValidation } from "./hooks/useContactDetailsValidation";
import { useSpeciesAndBreeds } from "./hooks/useSpeciesAndBreeds";

/**
 * Props for PatientInfoSection component.
 * @property onSpeciesSelected - Callback for when a species is selected
 * @property validationHook - The validation hook instance for managing patient form state and validation
 * @property speciesAndBreedsHook - The hook instance for managing species and breeds selection
 */
interface PatientInfoSectionProps {
  onSpeciesSelected?: (item: FormSelectItem) => void;
  validationHook: ReturnType<typeof useContactDetailsValidation>;
  speciesAndBreedsHook: ReturnType<typeof useSpeciesAndBreeds>;
}

/**
 * PatientInfoSection renders the patient information fields for the Provet contact details form.
 * Handles validation, error display, and state updates for each field.
 */
const PatientInfoSection = ({
  onSpeciesSelected,
  validationHook,
  speciesAndBreedsHook
}: PatientInfoSectionProps) => {
  const translationContext = useTranslationContext();
  const {
    patient: state,
    setPatient,
    patientValidation: validation,
    setPatientValidation: setValidation,
    formValidated,
    blurred,
    setBlurred,
    validateField,
    petNotesRef
  } = validationHook;
  const {
    speciesItems,
    onSpeciesSearch,
    selectedSpecies,
    breedItems,
    onBreedSearch,
    onBreedSelected,
    selectedBreedCode
  } = speciesAndBreedsHook;

  // Create refs to access latest state without triggering re-renders
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const PET_NAME_VALID_KEY: keyof OnlineBookingPatientValidation = "petNameValid";
  const PET_SPECIES_VALID_KEY: keyof OnlineBookingPatientValidation = "petSpeciesValid";
  const PET_NOTES_VALID_KEY: keyof OnlineBookingPatientValidation = "petNotesValid";

  // Check if species is valid by checking both validation state and actual data
  // This ensures preselected species are not shown as invalid
  const isSpeciesValid = validation.petSpeciesValid || !!selectedSpecies;

  // Pet Name handlers
  const handlePetNameChange = useCallback(
    (input: string) => {
      // Emoji don't count towards a valid value; they are removed when the field is left
      const value = removeEmoji(input);
      setPatient(patient => ({ ...patient, name: input }));
      setValidation(prev => ({
        ...prev,
        petNameValid: ProvetValidationUtilities.isValidPatientName(value)
      }));
    },
    [setPatient, setValidation]
  );

  const handlePetNameBlur = useCallback(async () => {
    setBlurred(prev => ({ ...prev, petName: true }));
    // Emoji are removed once the customer leaves the field
    setPatient(current => ({ ...current, name: removeEmoji(current.name ?? "") }));
    const valid = ProvetValidationUtilities.isValidPatientName(
      removeEmoji(stateRef.current.name ?? "")
    );
    await validateField(valid, setValidation, PET_NAME_VALID_KEY, "#pet-name");
  }, [setBlurred, validateField, setValidation, setPatient]);

  // Species handlers
  const handleSpeciesSearch = useCallback(
    (input?: string) => {
      if (input !== undefined) {
        onSpeciesSearch(input);
      }
    },
    [onSpeciesSearch]
  );

  const handleSpeciesBlur = useCallback(async () => {
    setBlurred(prev => ({ ...prev, petSpecies: true }));
    // Check if species is actually selected
    const valid = !!selectedSpecies;
    await validateField(valid, setValidation, PET_SPECIES_VALID_KEY, "#pet-species");
  }, [setBlurred, validateField, setValidation, selectedSpecies]);

  // Breed handlers
  const handleBreedSearch = useCallback(
    (input?: string) => {
      if (input !== undefined) {
        onBreedSearch(input);
      }
    },
    [onBreedSearch]
  );

  // Pet Notes handlers
  const handlePetNotesChange = useCallback(
    (input: string) => {
      // Emoji don't count towards a valid value; they are removed when the field is left
      const value = removeEmoji(input);
      setPatient(patient => ({ ...patient, notes: input }));
      setValidation(prev => ({
        ...prev,
        petNotesValid: ProvetValidationUtilities.isValidPatientNotes(value)
      }));
    },
    [setPatient, setValidation]
  );

  const handlePetNotesBlur = useCallback(async () => {
    setBlurred(prev => ({ ...prev, petNotes: true }));
    // Emoji are removed once the customer leaves the field
    setPatient(current => ({ ...current, notes: removeEmoji(current.notes ?? "") }));
    const valid = ProvetValidationUtilities.isValidPatientNotes(
      removeEmoji(stateRef.current.notes ?? "")
    );
    await validateField(valid, setValidation, PET_NOTES_VALID_KEY, "#pet-notes");
  }, [setBlurred, validateField, setValidation, setPatient]);

  return (
    <div className="flex flex-col gap-lg">
      {/* Pet Name Field */}
      <ValidatedTextInputField
        id="pet-name"
        label={translationContext?.translations?.petName}
        description={translationContext?.translations?.petNameDescription}
        errorMessage={translationContext?.translations?.petNameError}
        placeholder={translationContext?.translations?.petNamePlaceholder}
        value={state.name}
        required={true}
        isValid={validation.petNameValid}
        hasBlurred={blurred.petName}
        formValidated={formValidated}
        onValueChange={handlePetNameChange}
        onBlur={handlePetNameBlur}
      />

      {/* Species Field */}
      <ValidatedSelectInputField
        id="pet-species"
        label={translationContext?.translations?.species}
        errorMessage={translationContext?.translations?.speciesError}
        title={translationContext?.translations?.species}
        name={translationContext?.translations?.species}
        placeholder={translationContext?.translations?.speciesPlaceholder}
        searchPlaceholder={translationContext?.translations?.searchToFilter}
        items={speciesItems}
        value={selectedSpecies?.id?.toString()}
        required={true}
        isValid={isSpeciesValid}
        hasBlurred={blurred.petSpecies}
        formValidated={formValidated}
        onSearchCallback={handleSpeciesSearch}
        onSelectedChanged={onSpeciesSelected}
        onBlur={handleSpeciesBlur}
      />

      {/* Info Alert for Single Species */}
      {speciesItems.length === 1 && (
        <SimpleInfoAlert
          className="-mt-md"
          type={SimpleInfoAlertType.Info}
          text={translationContext?.translations?.speciesSingleTreatmentMessage}
        />
      )}

      {/* Breed Field (conditional) */}
      {selectedSpecies && (
        <ValidatedSelectInputField
          id="pet-breed"
          label={translationContext?.translations?.breed}
          errorMessage={translationContext?.translations?.breedError}
          title={translationContext?.translations?.breed}
          name={translationContext?.translations?.breed}
          placeholder={translationContext?.translations?.breedPlaceholder}
          searchPlaceholder={translationContext?.translations?.searchToFilter}
          items={breedItems}
          value={selectedBreedCode?.toString()}
          required={false}
          isValid={true}
          hasBlurred={false}
          formValidated={formValidated}
          onSearchCallback={handleBreedSearch}
          onSelectedChanged={onBreedSelected}
        />
      )}

      {/* Notes Field */}
      <ValidatedTextAreaField
        id="pet-notes"
        label={translationContext?.translations?.petNotes}
        description={translationContext?.translations?.petNotesDescription}
        errorMessage={translationContext?.translations?.petNotesError}
        placeholder={translationContext?.translations?.petNotesPlaceholder}
        value={state.notes}
        required={true}
        isValid={validation.petNotesValid}
        hasBlurred={blurred.petNotes}
        formValidated={formValidated}
        forwardedRef={petNotesRef}
        onValueChange={handlePetNotesChange}
        onBlur={handlePetNotesBlur}
      />
    </div>
  );
};

export default PatientInfoSection;
